<template>
  <div class="mt-3" ref="containerRef">
    <p class="text-sm font-semibold text-black mb-1">Email Flow</p>
    <div v-if="graph" class="w-full">
      <svg :width="W" :height="H" class="block" @mouseover="onSvgOver" @mouseleave="onSvgLeave">
        <!-- Background links: filled ribbons, dimmed when hovering -->
        <path
          v-for="(link, i) in graph.links"
          :key="'bg' + i"
          :d="linkPath(link)"
          :fill="linkColor(link)"
          :fill-opacity="hoverState ? 0.06 : 0.45"
          :data-li="i"
        />
        <!-- Highlight overlays: filled ribbon sub-bands aligned to the hovered flow -->
        <path
          v-for="(h, i) in (hoverState ? hoverState.linkOverlays : [])"
          :key="'hl' + i"
          :d="h.path"
          :fill="h.color"
          fill-opacity="0.7"
          style="pointer-events: none"
        />
        <g v-for="node in graph.nodes" :key="node.name">
          <!-- Full node rect, dimmed when hovering; no rx so link strokes meet it flush -->
          <rect
            :x="node.x0" :y="node.y0"
            :width="node.x1 - node.x0"
            :height="Math.max(1, node.y1 - node.y0)"
            :fill="nodeColor(node)"
            :fill-opacity="hoverState ? 0.15 : 1"
            :data-node="node.name"
          />
          <!-- One highlight rect per distinct range through this node -->
          <template v-if="nodeRange(node.name)">
            <rect
              v-for="(r, ri) in nodeRange(node.name).ranges"
              :key="'nr' + ri"
              :x="node.x0"
              :y="r.y0"
              :width="node.x1 - node.x0"
              :height="Math.max(2, r.y1 - r.y0)"
              :fill="nodeColor(node)"
              stroke="rgba(255,255,255,0.6)"
              stroke-width="1"
              rx="1"
              :data-node="node.name"
            />
          </template>
          <text
            :x="node.x1 + 6"
            :y="nodeRange(node.name)?.labelY ?? (node.y0 + node.y1) / 2"
            dominant-baseline="middle" font-size="11" fill="#4b5563"
            :opacity="hoverState && !nodeRange(node.name) ? 0.3 : 1"
          >{{ node.name }} <tspan fill="#374151">({{ nodeRange(node.name)?.totalCount ?? node.value }})</tspan></text>
        </g>
      </svg>
    </div>
    <p v-else class="text-xs text-gray-400 italic">No email flow data for this campaign.</p>
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { sankey, sankeyJustify } from 'd3-sankey'

const MIN_W = 400
const H = 500

// Filled ribbon: two cubic bezier edges forming a closed band.
function ribbonPath(x0, y0, x1, y1, w) {
  const h = w / 2
  const cx = (x0 + x1) / 2
  const ex0 = x0 - 1
  const ex1 = x1 + 1
  return (
    `M${ex0},${y0 - h}` +
    ` C${cx},${y0 - h} ${cx},${y1 - h} ${ex1},${y1 - h}` +
    ` L${ex1},${y1 + h}` +
    ` C${cx},${y1 + h} ${cx},${y0 + h} ${ex0},${y0 + h}Z`
  )
}

export default {
  props: {
    // [{ from, to, count }], pre-aggregated server-side
    edges: { type: Array, default: () => [] },
    // { [nodeName]: '#hex' }, supplied by the parent
    nodeColors: { type: Object, default: () => ({}) },
  },
  setup(props) {
    const getEmailStage = (nodeName) => {
      // Extract "Email 1" or "Email Sent" from "Email 1 - Template Name"
      const match = nodeName.match(/^(Email \d+|Email Sent)/)
      return match ? match[1] : nodeName
    }

    const nodeColor = (node) => {
      const stage = getEmailStage(node.name)
      return props.nodeColors[stage] ?? '#607D8B'
    }
    const linkColor = (link) => {
      const stage = getEmailStage(link.source.name)
      return props.nodeColors[stage] ?? '#607D8B'
    }

    const containerRef = ref(null)
    const containerWidth = ref(MIN_W)
    const W = computed(() => Math.max(MIN_W, containerWidth.value))
    let resizeObserver = null

    onMounted(() => {
      if (containerRef.value) {
        resizeObserver = new ResizeObserver(entries => {
          const width = entries[0]?.contentRect?.width
          if (width) containerWidth.value = Math.round(width)
        })
        resizeObserver.observe(containerRef.value)
      }
    })

    onBeforeUnmount(() => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    })

    const hoveredLink = ref(null)
    const hoveredNode = ref(null)

    const hoverState = computed(() => {
      if (!graph.value) return null
      const links = graph.value.links
      const linkOverlays = []
      const nodeRanges = new Map()

      function addNodeRange(node, centerY, height, count) {
        const y0 = Math.max(node.y0, centerY - height / 2)
        const y1 = Math.min(node.y1, centerY + height / 2)
        if (nodeRanges.has(node.name)) {
          const e = nodeRanges.get(node.name)
          e.ranges.push({ y0, y1, count })
          e.totalCount += count
        } else {
          nodeRanges.set(node.name, { ranges: [{ y0, y1, count }], totalCount: count, labelY: (y0 + y1) / 2 })
        }
      }

      function downstream(node, visited) {
        links.forEach((l, i) => {
          if (l.source !== node || visited.has(i)) return
          visited.add(i)
          linkOverlays.push({ path: ribbonPath(l.source.x1, l.y0, l.target.x0, l.y1, l.width), color: props.nodeColors[l.source.name] ?? '#607D8B' })
          addNodeRange(l.target, l.y1, l.width, l.value)
          downstream(l.target, visited)
        })
      }

      function upstreamNarrow(node, entryY, hw, value, visited) {
        links.forEach((l, i) => {
          if (l.target !== node || visited.has(i)) return
          visited.add(i)
          const subSourceY = l.y0 + (entryY - l.y1)
          linkOverlays.push({ path: ribbonPath(l.source.x1, subSourceY, l.target.x0, entryY, hw), color: props.nodeColors[l.source.name] ?? '#607D8B' })
          addNodeRange(l.source, subSourceY, hw, value)
          upstreamNarrow(l.source, subSourceY, hw, value, visited)
        })
      }

      if (hoveredNode.value !== null) {
        const anchor = graph.value.nodes.find(n => n.name === hoveredNode.value)
        if (!anchor) return null

        addNodeRange(anchor, (anchor.y0 + anchor.y1) / 2, anchor.y1 - anchor.y0, anchor.value)

        links.forEach((l, i) => {
          if (l.target !== anchor) return
          linkOverlays.push({ path: ribbonPath(l.source.x1, l.y0, l.target.x0, l.y1, l.width), color: props.nodeColors[l.source.name] ?? '#607D8B' })
          addNodeRange(l.source, l.y0, l.width, l.value)
          upstreamNarrow(l.source, l.y0, l.width, l.value, new Set([i]))
        })

        downstream(anchor, new Set())
        return { linkOverlays, nodeRanges }
      }

      if (hoveredLink.value !== null) {
        const root = links[hoveredLink.value]
        const hw = root.width

        linkOverlays.push({ path: ribbonPath(root.source.x1, root.y0, root.target.x0, root.y1, hw), color: props.nodeColors[root.source.name] ?? '#607D8B' })
        addNodeRange(root.source, root.y0, hw, root.value)
        addNodeRange(root.target, root.y1, hw, root.value)

        upstreamNarrow(root.source, root.y0, hw, root.value, new Set([hoveredLink.value]))
        downstream(root.target, new Set([hoveredLink.value]))
        return { linkOverlays, nodeRanges }
      }

      return null
    })

    const flowData = computed(() => {
      if (!props.edges.length) return null
      const nodeNames = new Set()
      for (const e of props.edges) {
        nodeNames.add(e.from)
        nodeNames.add(e.to)
      }
      return {
        nodes: [...nodeNames].map(name => ({ name })),
        links: props.edges.map(e => ({ source: e.from, target: e.to, value: e.count })),
      }
    })

    const graph = computed(() => {
      if (!flowData.value || !flowData.value.nodes.length || !flowData.value.links.length) return null
      const { nodes, links } = flowData.value
      try {
        const layout = sankey()
          .nodeId(d => d.name)
          .nodeWidth(14)
          .nodePadding(14)
          .nodeAlign(sankeyJustify)
          .extent([[10, 10], [Math.max(400, W.value - 200), H - 10]])
        return layout({ nodes: nodes.map(d => ({ ...d })), links: links.map(d => ({ ...d })) })
      } catch (e) {
        console.error('Sankey layout error:', e)
        return null
      }
    })

    const nodeRange = (name) => hoverState.value?.nodeRanges.get(name) ?? null

    const linkPath = (link) => ribbonPath(link.source.x1, link.y0, link.target.x0, link.y1, link.width)

    const onSvgOver = (e) => {
      const li = e.target.dataset.li
      const nodeName = e.target.dataset.node
      if (li !== undefined) {
        const next = parseInt(li, 10)
        if (hoveredLink.value !== next) hoveredLink.value = next
        if (hoveredNode.value !== null) hoveredNode.value = null
      } else if (nodeName !== undefined) {
        if (hoveredNode.value !== nodeName) hoveredNode.value = nodeName
        if (hoveredLink.value !== null) hoveredLink.value = null
      } else {
        if (hoveredLink.value !== null) hoveredLink.value = null
        if (hoveredNode.value !== null) hoveredNode.value = null
      }
    }

    const onSvgLeave = () => { hoveredLink.value = null; hoveredNode.value = null }

    return {
      containerRef,
      graph,
      hoverState,
      nodeRange,
      onSvgOver,
      onSvgLeave,
      W,
      H,
      linkPath,
      nodeColor,
      linkColor,
    }
  },
}
</script>
